import React, { useState, useRef } from 'react';
import { IoMdCheckmarkCircle } from 'react-icons/io';
import { FaRegFilePdf } from 'react-icons/fa';
import { generatePDF } from './pdfGenerator';
import NewClassificacaoPDF from './classificacao/newClassificacaoPDF.js';
import NewInvestigacaoPDF from './investigacao/newInvestigacaoPDF.js';
import NewNotificacaoPDF from './notificacao/newNotificacaoPDF.js';
import NewAvaliacaoPDF from './avaliacao/newAvaliacaoPDF';

const FINISH_CARD_CONTENT = {
	classificacao: {
		title: 'Classificação Enviada com Sucesso',
		description:
			'Sua classificação foi enviada para a equipe de investigação. Acompanhe o andamento através do protocolo abaixo.',
		pdfLabel: 'classificação',
	},
	investigacao: {
		title: 'Investigação Enviada com Sucesso',
		description:
			'Sua investigação foi enviada para a etapa de avaliação final. Acompanhe o andamento através do protocolo abaixo.',
		pdfLabel: 'investigação',
	},
	avaliacaoFinal: {
		title: 'Notificação Concluída com Sucesso',
		description:
			'Esta notificação foi concluída com sucesso. Você pode consultar os detalhes pelo protocolo abaixo.',
		pdfLabel: 'notificação concluída',
	},
	conclusao: {
		title: 'Conclusão Enviada com Sucesso',
		description:
			'Esta notificação foi concluída com sucesso. Você pode consultar os detalhes pelo protocolo abaixo.',
		pdfLabel: 'conclusão',
	},
	notificacao: {
		title: 'Notificação Enviada com Sucesso',
		description:
			'Sua notificação foi enviada para a equipe de classificação. Acompanhe o andamento através do protocolo abaixo.',
		pdfLabel: 'notificação',
	},
};

const inferFinishStage = (stage, status) => {
	if (stage && FINISH_CARD_CONTENT[stage]) {
		return stage;
	}

	switch (status) {
		case 'NOTIFICADO':
			return 'classificacao';
		case 'CLASSIFICADO':
		case 'REINVESTIGACAO':
			return 'investigacao';
		case 'INVESTIGADO':
			return 'avaliacaoFinal';
		case 'CONCLUIDO':
			return 'conclusao';
		default:
			return 'notificacao';
	}
};

const FinishCard = ({ requestResult, stage }) => {
	const [copiado, setCopiado] = useState(false);
	const [pdfBaixado, setPdfBaixado] = useState(false);
	const currentStage = inferFinishStage(
		stage,
		requestResult?.notificacao?.status,
	);
	const currentContent =
		FINISH_CARD_CONTENT[currentStage] || FINISH_CARD_CONTENT.notificacao;

	// refs separados: um para cada componente/PDF
	const notifPdfRef = useRef(null);
	const classPdfRef = useRef(null);
	const investigacaoPdfRef = useRef(null);
	const avaliacaoPdfRef = useRef(null);
	const avaliacaoPdfReadyRef = useRef(false);
	const isAvaliacaoFinal = currentStage === 'avaliacaoFinal';
	const shouldIncludeInvestigacaoPdf =
		!isAvaliacaoFinal &&
		(currentStage === 'investigacao' ||
			Boolean(requestResult?.investigacao));

	const waitForAvaliacaoPdfReady = async () => {
		const startedAt = Date.now();
		while (
			!avaliacaoPdfReadyRef.current &&
			Date.now() - startedAt < 15000
		) {
			await new Promise((resolve) => setTimeout(resolve, 100));
		}
	};

	const handleDownloadPdf = async () => {
		const refs = isAvaliacaoFinal
			? [avaliacaoPdfRef]
			: [
					notifPdfRef,
					classPdfRef,
					...(shouldIncludeInvestigacaoPdf
						? [investigacaoPdfRef]
						: []),
				];
		if (isAvaliacaoFinal) {
			await waitForAvaliacaoPdfReady();
			await new Promise((resolve) => requestAnimationFrame(resolve));
		}
		await generatePDF(
			refs,
			`${requestResult?.notificacao?.protocolo || 'notificacao'}.pdf`,
		);
		setPdfBaixado(true);
	};

	return (
		<div className='font-inter flex justify-center items-start w-[80%] mx-auto h-screen z-20'>
			<div className='flex flex-col bg-[#F5F5F5] mt-[100px] items-center rounded-md max-w-[800px] gap-5 flex-grow shadow-lg '>
				<div className='bg-[#004A80] rounded-t-md w-full h-[20px]'></div>

				<div className='text-[#00C0F3]'>
					<IoMdCheckmarkCircle className='size-[50px] sm:size-[100px]' />
				</div>

				<div className='text-3xl font-semibold text-center text-[#004A80]'>
					{currentContent.title}
				</div>

				<div className='text-lg font-medium w-[80%] text-[#8b8b8b]'>
					{currentContent.description}
				</div>

				<div className='flex flex-col sm:text-4xl font-bold mt-2 sm:mt-[32px] text-[#004A80]'>
					<button
						onClick={() => {
							setCopiado(true);
							navigator.clipboard.writeText(
								requestResult?.notificacao?.protocolo,
							);
						}}
						className='flex gap-3'>
						<p>
							Protocolo: {requestResult?.notificacao?.protocolo}
						</p>
					</button>
					{copiado && (
						<div className='flex flex-col justify-end'>
							<div className='text-[#a0a0a0] font-semibold text-end text-[12px]'>
								Protocolo copiado!
							</div>
						</div>
					)}
				</div>

				<>
					<p className='text-xs sm:text-lg mx-auto text-center sm:text-start text-[#a0a0a0] mt-4'>
						Clique no botão abaixo para baixar o PDF da sua{' '}
						{currentContent.pdfLabel}
					</p>
					<button
						className='sm:w-35 flex items-center justify-center gap-2 border border-[#B74644] text-[#B74644] p-1 sm:py-2 sm:px-4 rounded-md hover:bg-[#B74644] hover:text-white transition-all duration-200 text-[10px] sm:text-base focus:outline-none cursor-pointer sm:mb-[40px]'
						onClick={handleDownloadPdf}>
						{pdfBaixado ? (
							'Download Concluído'
						) : (
							<>
								<FaRegFilePdf className='scale-[130%] text-[10px]' />{' '}
								Baixar PDF
							</>
						)}
					</button>
				</>

				<a
					className='text-[#00C0F3] text-[10px] sm:text-base mb-14 hover:text-[#004A80] transition-all duration-200'
					href='/hub-de-notificacoes'>
					voltar à consulta de protocolos
				</a>
			</div>

			{/* Componentes de PDF escondidos, cada um com sua própria ref */}
			<div
				style={{
					position: 'absolute',
					left: '-9999px',
					top: 0,
				}}>
				{isAvaliacaoFinal ? (
					<div ref={avaliacaoPdfRef}>
						<NewAvaliacaoPDF
							notificacao={requestResult?.notificacao}
							avaliacao={requestResult?.avaliacao}
							onReady={() => {
								avaliacaoPdfReadyRef.current = true;
							}}
						/>
					</div>
				) : (
					<>
						<div
							className=' no-break '
							ref={notifPdfRef}>
							<NewNotificacaoPDF
								notificacao={requestResult?.notificacao}
							/>
						</div>
						<div
							className=' no-break '
							ref={classPdfRef}>
							<NewClassificacaoPDF
								classificacao={requestResult?.classificacao}
								notificacao={requestResult?.notificacao}
							/>
						</div>
						{shouldIncludeInvestigacaoPdf && (
							<div
								className=' no-break '
								ref={investigacaoPdfRef}>
								<NewInvestigacaoPDF
									notificacao={requestResult?.notificacao}
									investigacao={requestResult?.investigacao}
								/>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default FinishCard;
